import { request } from '@reflex/ui/client/http';
import type { GitHubRepoListResponse, GitHubBranchListResponse } from '@reflex/shared';
import type { GitHubIssueSearchResponse } from '../shared/issue-search.js';

export interface GitHubAppInstallationView {
  id: string;
  installationId: number;
  accountLogin: string;
  accountType: 'User' | 'Organization';
  repositorySelection: 'all' | 'selected';
  suspendedAt: number | null;
  createdAt: number;
}

export interface GitHubAppView {
  id: string;
  appId: number;
  slug: string;
  name: string;
  htmlUrl: string | null;
  source: 'manifest' | 'manual' | 'env';
  organizationId: string | null;
  createdAt: number;
  hasPrivateKey: boolean;
  hasWebhookSecret: boolean;
  hasClientSecret: boolean;
  installations: GitHubAppInstallationView[];
}

export const githubApi = {
  searchGitHubRepos: (query?: string, scope?: 'user' | 'installation', perPage?: number) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (scope === 'installation') params.set('scope', scope);
    if (perPage !== undefined) params.set('per_page', String(perPage));
    const qs = params.toString();
    return request<GitHubRepoListResponse>(`/github/repos${qs ? `?${qs}` : ''}`);
  },
  /**
   * The caller's whole accessible repo list in one request, so the client can
   * filter it locally instead of asking the server per keystroke. The response
   * reports `complete` — see `useGitHubRepos`, which owns this contract.
   */
  listGitHubRepos: (scope: 'user' | 'installation', perPage: number) =>
    githubApi.searchGitHubRepos(undefined, scope, perPage),
  getGitHubBranches: (owner: string, repo: string, scope?: 'user' | 'installation') => {
    const params = new URLSearchParams();
    if (scope === 'installation') params.set('scope', scope);
    const qs = params.toString();
    return request<GitHubBranchListResponse>(
      `/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches${qs ? `?${qs}` : ''}`,
    );
  },
  /**
   * Search pull requests (`type: 'pr'`) or issues (`type: 'issue'`) visible
   * to the org's GitHub App installation, falling back to the caller's OAuth
   * token server-side. Backs the `@pr:` / `@issue:` agent-context categories.
   */
  searchGitHubIssues: (query: string, type: 'pr' | 'issue') => {
    const params = new URLSearchParams({ type });
    if (query) params.set('q', query);
    return request<GitHubIssueSearchResponse>(`/github/search/issues?${params.toString()}`);
  },
  /**
   * Resolve a pasted PR link to its repo slug + head branch, plus the live
   * facts (`title`, `state`, `merged`, `draft`, and the head/base branches) the
   * PR chip uses to reflect the real state and fill its hover card.
   */
  resolvePullRequest: (owner: string, repo: string, number: number) =>
    request<{
      repoSlug: string;
      repoBranch: string;
      title: string;
      state: 'open' | 'closed';
      merged: boolean;
      draft: boolean;
      headBranch: string;
      baseBranch: string;
      updatedAt: number | null;
    }>(`/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/pulls/${number}`),

  /**
   * Batch-resolve several pull requests in the same repo in a single request —
   * the server-side counterpart to {@link resolvePullRequest}, built to collapse
   * the Sentry `AGENT-FLOW-WEB-4F` / `4G` N+1 (N PR chips on a focused agent page
   * each firing their own `/pulls/:number` fetch). Returns exactly the
   * chip-facing subset (`title`/`state`/`merged`/`draft`/`headBranch`/
   * `baseBranch`); a 404'd PR is omitted rather than reported, so the chip can
   * fall back to its stored association state for the miss.
   */
  resolvePullRequestsBatch: (
    owner: string,
    repo: string,
    numbers: number[],
  ): Promise<{
    pulls: Array<{
      number: number;
      title: string;
      state: 'open' | 'closed';
      merged: boolean;
      draft: boolean;
      headBranch: string;
      baseBranch: string;
      updatedAt: number | null;
    }>;
  }> =>
    request<{
      pulls: Array<{
        number: number;
        title: string;
        state: 'open' | 'closed';
        merged: boolean;
        draft: boolean;
        headBranch: string;
        baseBranch: string;
        updatedAt: number | null;
      }>;
    }>(`/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/pulls/batch`, {
      method: 'POST',
      body: JSON.stringify({ numbers }),
    }),

  listGitHubApps: () => request<GitHubAppView[]>('/github/apps'),
  /**
   * Audit H12: mint a CSRF-bound install URL for the active org. The
   * server stamps a one-shot nonce into both the returned URL's
   * `state` parameter and an HttpOnly `gh_install_csrf` cookie, then
   * verifies them on the setup callback.
   */
  beginGitHubAppInstall: (installUrl: string) =>
    request<{ installUrl: string }>('/github/app/install/begin', {
      method: 'POST',
      body: JSON.stringify({ installUrl }),
    }),
  deleteGitHubApp: (id: string) =>
    request<void>(`/github/apps/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  removeGitHubInstallation: (installationId: number) =>
    request<void>(`/github/installations/${installationId}`, {
      method: 'DELETE',
    }),
};
